import type { FieldValueDecorations } from '@jira/polaris__domain-field/src/ValueDecoration.types.tsx';
import type { Field, FieldMap, FieldKey } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import type { PolarisPlay } from '@jira/polaris__domain-field/src/PolarisPlayContribution.types.tsx';
import type { SnippetProvider } from '@jira/polaris__domain-field/src/SnippetProvider.types.tsx';
import type { FieldRemote } from '@jira/polaris__remote-field/src/FieldRemote.types.tsx';
import type { GlobalFieldRemote } from '@jira/polaris__remote-global-field/src/useGlobalFieldRemote.tsx';
import type { Ari } from '@jira/platform__utils-ari/src/index.tsx';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

type IssueTypeId = string;

export const DEFAULT_OPTION_WEIGHT = 1;

export type ArchivedFieldsConfig = {
	archivedField: Field | undefined;
	archivedByField: Field | undefined;
	archivedOnField: Field | undefined;
};

/**
 * Container props that when changed should cause state to be refreshed
 */
type LoadingProps = {
	containerAri: Ari;
	isProjectOnboarded: boolean | undefined;
};

/**
 * Container props for the field container
 */
export type Props = LoadingProps & {
	fieldRemote: FieldRemote;
	globalFieldRemote: GlobalFieldRemote;

	// not a remote action -> deletes play from project store, local state action only!
	deletePlay: (play: PolarisPlay) => void;

	projectId: string | undefined;
	cloudId: string;
	isAtlasIntegrationEnabled: boolean;

	hasNoProjectPermissions: boolean;

	issueTypeIds: IssueTypeId[] | undefined;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	onFieldLoadingFailed: (arg1: Error) => void;
	onDecorationUpdateFailed: (arg1: Error) => void;
	onFieldUpdateFailed: (arg1: Error) => void;
	onActionFailed: (arg1: Error) => void;
	/** Callback to be called when field with cyclic formula is detected */
	onCyclicFormulaDependencyDetected: (fieldLabels: string[]) => void;
	isSiteAdmin: boolean;
	isAnonymousUser?: boolean;
	isCollectionView?: boolean;
	isSharedView?: boolean;
};

export const NEW_FIELD_CREATED_IN_PROJECT: unique symbol = Symbol('NEW_FIELD_CREATED_IN_PROJECT');
export const SINGLE_GLOBAL_FIELD_ADDED_TO_PROJECT: unique symbol = Symbol(
	'SINGLE_GLOBAL_FIELD_ADDED_TO_PROJECT',
);
export const MANY_GLOBAL_FIELDS_ADDED_TO_PROJECT: unique symbol = Symbol(
	'MANY_GLOBAL_FIELDS_ADDED_TO_PROJECT',
);

// For now it seems like we only handle field addition from the UI notifications perspective (row highlighting, toast notifications)
export type FieldsUpdate =
	| {
			type: typeof NEW_FIELD_CREATED_IN_PROJECT;
			fieldKey: FieldKey;
			timestamp: number;
	  }
	| {
			type: typeof SINGLE_GLOBAL_FIELD_ADDED_TO_PROJECT;
			globalFieldKey: FieldKey;
			inputFieldKeys: FieldKey[];
			timestamp: number;
	  }
	| {
			type: typeof MANY_GLOBAL_FIELDS_ADDED_TO_PROJECT;
			mapOfGlobalFieldKeysToTheirInputFieldKeys: Map<FieldKey, FieldKey[]>;
			timestamp: number;
	  };

export type State = {
	/**
	 * meta information on status of the issue state. does not contain any issue
	 * data; contains additional indicators on to the overall state of the state
	 */
	meta: {
		initialized: boolean;
		loading: boolean;
		loadingSnippetProviders: boolean;
		/**
		 * field -> issueType[] associations that are currently being loaded; needed for
		 * cross project issue linking to work properly
		 */
		connectionsLoadingFieldsAssociations: FieldKey[];
		error: Error | undefined;
		projectIsNotOnboardedError: boolean;
		scheduleFieldsReload: boolean;
		loadingProps: LoadingProps | undefined;
	};
	fields: FieldMap;
	fieldValueDecorations: FieldValueDecorations;
	snippetProviders: SnippetProvider[] | undefined;
	throttledFetchSnippetProviders: ((refetch: boolean, archivedOnly: false) => void) | undefined;

	lastFieldsUpdate: FieldsUpdate | null;

	/**
	 * We keep track of these to be able to determine whether we should refresh loaded issues
	 * If a newly added field is already in use in a view, we refresh to fetch its values
	 */
	newlyAddedGlobalFields: FieldKey[];
	/**
	 * props mirror for correct selector and hook handling
	 */
	containerProps: Props | undefined;
	prevContainerProps?: Props;
	/**
	 * Stores additional field -> issueType[] associations for issue types that are not part
	 * of the current container (roadmap / project). Needed for cross project issue linking
	 * to work properly
	 */
	connectionsFieldsAssociations: Record<FieldKey, IssueTypeId[]>;
};
